'use client'

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { useToaster } from '../context/ToasterContext';
import Spinner from "../components/Spinner";
import Button from '../components/Button';
import Input from '../components/Input';
import Table from '../components/Table';
import ConfirmationModal from '../components/ConfirmationModal';
import api from '../api/axios';
import { useSelector } from 'react-redux';

export default function OptOutSearch() {
  const { toaster } = useToaster();
  const templates = useSelector((s) => s.globalData.templates) ?? [];
  const templateOptions = templates.map((t) => ({ value: t.id, label: t.name }));

  const [numberQuery, setNumberQuery] = useState('');
  const [templateQuery, setTemplateQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const formatDate = (d) => {
    if (!d) return '—';
    const date = typeof d === 'string' || typeof d === 'number' ? new Date(d) : d;
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleString();
  };

  const onSearch = async (e) => {
    if (e) e.preventDefault();

    const number = numberQuery.trim();
    const templateId = templateQuery.trim();

    // Exclusive guard
    if (!number && !templateId) {
      toaster.error('Please enter a phone number or select a template.');
      return;
    }
    if (number && templateId) {
      toaster.error('Please enter either a phone number OR a template (not both).');
      return;
    }

    const endpoint = templateId
      ? `${process.env.REACT_APP_BASE_API_URL}/mvapi/utils/contacts/template/${encodeURIComponent(templateId)}`
      : `${process.env.REACT_APP_BASE_API_URL}/mvapi/utils/contacts/number/${encodeURIComponent(number)}`;

    try {
      setLoading(true);
      const { data } = await api.get(endpoint);
      setResults(Array.isArray(data) ? data : (data ? [data] : []));
    } catch (err) {
      console.error(err);
      toaster.error('Search failed. Please try again.');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const initDelete = (item) => {
    setSelectedItem(item);
    setShowConfirmationModal(true);
  };

  const doDelete = async () => {
    if (!selectedItem) return;
    try {
      await api.delete(`${process.env.REACT_APP_BASE_API_URL}/mvapi/utils/contacts/${selectedItem.id}`);
      toaster.success('Contact preference deleted successfully!');
      await onSearch(); // refresh current results
      setShowConfirmationModal(false);
    } catch (error) {
      console.error(error);
      toaster.error('Delete failed. Please try again.');
    }
  };

  const ChannelBadge = ({ label, on }) => (
    <span
      className={[
        "inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset",
        on
          ? "bg-red-50 text-red-700 ring-red-600/20 dark:bg-red-900/20 dark:text-red-300 dark:ring-red-500/30"
          : "bg-gray-50 text-gray-500 ring-gray-500/10 dark:bg-gray-900/40 dark:text-gray-400 dark:ring-gray-600/30"
      ].join(' ')}
    >
      {label}
    </span>
  );

  return (
    <>
      {loading && <Spinner />}

      <div className="px-4 sm:px-6 lg:px-8">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h2 className="text-2xl/7 font-bold text-gray-900 dark:text-gray-100 sm:truncate sm:text-3xl sm:tracking-tight">
              Opt-Out Search
            </h2>
            <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
              Look up by phone number or template ID to view and manage contact preferences / opt-outs.
            </p>
          </div>
        </div>

        {/* Important note */}
        <div
          role="alert"
          className="mt-4 rounded-lg border border-red-500/30 bg-red-50 dark:bg-red-900/20 p-4 ring-1 ring-red-500/20"
        >
          <div className="flex">
          <InformationCircleIcon className="h-5 w-5 text-red-700 dark:text-red-300 mt-0.5" />            <div className="ml-3">
              <h3 className="text-sm font-semibold text-red-900 dark:text-red-200">Important</h3>
              <p className="mt-1 text-sm text-emerald-800 dark:text-emerald-300">
                Highlighted channels indicate that the user has opted out of that channel.
              </p>
            </div>
          </div>
        </div>

        {/* Search */}
        <form onSubmit={onSearch} className="mt-6 grid grid-cols-1 sm:grid-cols-12 gap-3">
          {/* Phone Number */}
          <div className="sm:col-span-5">
            <Input
              label="Phone Number"
              placeholder="e.g., +14155551234"
              inputMode="tel"
              value={numberQuery}
              onChange={(e) => setNumberQuery(e.target.value)}
            />
          </div>

          {/* Template */}
          <div className="sm:col-span-5">
            <Input
              as="combobox"
              label="Template"
              placeholder="Select a template"
              options={templateOptions}
              value={templateQuery}
              onChange={(e) => setTemplateQuery(e.target.value)}
            />
          </div>

          {/* Buttons */}
          <div className="sm:col-span-2 flex items-end gap-3">
            <Button type="submit" variant="primary" className="w-full">Search</Button>
            <Button
              type="button"
              variant="secondary"
              className="w-full"
              onClick={() => {
                setNumberQuery('');
                setTemplateQuery('');
                setResults([]);
              }}
            >
              Clear
            </Button>
          </div>
        </form>

        {/* Results */}
        <div className="mt-8">
          <Table
            data={results}
            columns={[
              { key: 'number', title: 'Number', render: (r) => r.number ?? '—' },
              { key: 'target', title: 'Target', render: (r) => r.target ?? '—' },
              { key: 'templateId', title: 'Template ID', render: (r) => r.templateId ?? '—' },
              {
                key: 'channels',
                title: 'Channels',
                render: (r) => (
                  <div className="flex gap-2">
                    <ChannelBadge label="SMS" on={!!r.sms} />
                    <ChannelBadge label="WhatsApp" on={!!r.whatsapp} />
                    <ChannelBadge label="MMS" on={!!r.mms} />
                  </div>
                )
              },
              { key: 'insertDttm', title: 'Inserted', render: (r) => formatDate(r.insertDttm) },
              { key: 'updateDttm', title: 'Updated', render: (r) => formatDate(r.updateDttm) },
              {
                key: 'actions',
                title: 'Actions',
                align: 'right',
                render: (item) => (
                  <div className="space-x-2">
                    <Link
                      to={`/edit-contact/${item.id}`}
                      className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-900 dark:hover:text-emerald-100 mr-5"
                    >
                      Edit
                    </Link>
                    <button
                      type="button"
                      onClick={() => initDelete(item)}
                      className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-100"
                    >
                      Delete
                    </button>
                  </div>
                )
              }
            ]}
            emptyState={{
              title: results.length === 0 ? 'No results yet' : 'No records found',
              description: results.length === 0
                ? 'Search by phone number or template.'
                : 'Try another query.'
            }}
          />
        </div>
      </div>

      <ConfirmationModal
        open={showConfirmationModal}
        title="Delete contact preference"
        description="Are you sure you want to delete? This action cannot be undone."
        confirmText="Yes"
        cancelText="No"
        onConfirm={doDelete}
        onCancel={() => setShowConfirmationModal(false)}
      />
    </>
  );
}
